<script setup lang="ts">
import { pollTailwindClasses, toOzBoxColor } from '@@/bits/post_color'
import { isPostScheduled } from '@@/bits/post_state'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Poll, Post, PostColor, User } from '@@/types'
import SurfacePostPollBottomSection from '@@/vuecomponents/SurfacePostPollBottomSection.vue'
import SurfacePostPollChoice, {
  SurfacePostPollChoiceInteractionsDisabled,
} from '@@/vuecomponents/SurfacePostPollChoice.vue'
import SurfacePostPollChoiceWhiteboard from '@@/vuecomponents/SurfacePostPollChoiceWhiteboard.vue'
import SurfacePostPollQuestion from '@@/vuecomponents/SurfacePostPollQuestion.vue'
import SurfacePostPollResult from '@@/vuecomponents/SurfacePostPollResult.vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, toRefs } from 'vue'

const emit = defineEmits<{
  (e: 'dblclick', event: MouseEvent): void
  (e: 'click', event: MouseEvent): void
  (e: 'enter', event: KeyboardEvent): void
  (e: 'load')
}>()

const props = withDefaults(
  defineProps<{
    poll: Poll
    pollCaption?: string
    post: Post
    backgroundColor?: PostColor
    user: User
    darkMode?: boolean | 'auto'
    displayMode?: 'expanded' | 'default' | 'slideshow'
    xOverlay?: boolean
  }>(),
  {
    darkMode: 'auto',
    backgroundColor: undefined,
    pollCaption: '',
    displayMode: 'default',
    xOverlay: false,
  },
)

const { darkMode, pollCaption, poll, displayMode, xOverlay } = toRefs(props)

const isDisplayModeDefault = computed(() => displayMode.value === 'default')
const isDisplayModeExpanded = computed(() => displayMode.value === 'expanded')
const isDisplayModeSlideshow = computed(() => displayMode.value === 'slideshow')
const isDisplayModeExpandedOrSlideshow = computed(() => isDisplayModeExpanded.value || isDisplayModeSlideshow.value)
const { isSmallerThanDesktop3XL, isSmallerThanDesktop2XL } = storeToRefs(useWindowSizeStore())
const pollScaleMultiplier = computed(() => {
  if (!isSmallerThanDesktop3XL.value) {
    return 2.5
  } else if (!isSmallerThanDesktop2XL.value) {
    return 2
  } else {
    return 1
  }
})
const displayedBackgroundColor = computed<PostColor>(() =>
  props.backgroundColor === undefined ? props.post.color : props.backgroundColor,
)
const pollColors = computed(() =>
  pollTailwindClasses(isDisplayModeExpandedOrSlideshow.value ? null : displayedBackgroundColor.value),
)
const { isStream, isWhiteboard, isFrozen } = storeToRefs(useSurfaceStore())
const { postPollVote } = useSurfacePostsStore()
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const isPostAuthor = computed(() => props.post.author_id === props.user.id)
const totalVotesArray = computed(() => {
  return poll.value.poll_choices.map((choice) => choice.total_votes)
})

const totalVotesCount = computed(() => {
  return totalVotesArray.value.reduce((a, b) => {
    if (typeof a !== 'number' || typeof b !== 'number') return 0
    return a + b
  }, 0)
})

const highestVoteTotal = computed(() => Math.max(...totalVotesArray.value))
const isPollClosed = computed(() => poll.value.closed_at != null)
const pollResults = computed(() => {
  return poll.value.poll_choices.map((pollChoice) => {
    return {
      ...pollChoice,
      percentage: totalVotesCount.value === 0 ? 0 : (pollChoice.total_votes / totalVotesCount.value) * 100,
      pollWinner:
        pollChoice.total_votes === highestVoteTotal.value && isPollClosed.value && pollChoice.total_votes !== 0,
    }
  })
})

const didCurrentUserVoteForPoll = computed(() => {
  return poll.value.user_voted
})

const hasDecimalVoteRatio = computed(() => pollResults.value.some((pollChoice) => pollChoice.percentage % 1 !== 0))
const selectedPollChoiceId = ref(-1)
const isShowingPollResults = ref(false)
const isVoting = ref(false)
const interactionsDisabled = computed<SurfacePostPollChoiceInteractionsDisabled>(() => {
  if (isPostScheduled(props.post)) return SurfacePostPollChoiceInteractionsDisabled.All
  if (isFrozen.value) return SurfacePostPollChoiceInteractionsDisabled.Select
  return SurfacePostPollChoiceInteractionsDisabled.None
})

const bottomSectionHeight = ref(isDisplayModeExpandedOrSlideshow.value ? 48 : 40)
const pollResultsContainer = ref()
const pollScrollableContainer = ref()
const togglePollResults = () => {
  isShowingPollResults.value = !isShowingPollResults.value

  if (isShowingPollResults.value) {
    const pollResultsContainerTop = pollResultsContainer.value.offsetTop
    pollScrollableContainer.value.scrollTo({ top: pollResultsContainerTop, behavior: 'smooth' })
  }
}

const vote = async () => {
  isVoting.value = true
  await postPollVote({
    wishId: props.post.id!,
    pollId: poll.value.poll_id,
    pollChoiceIds: [selectedPollChoiceId.value],
  })
  isVoting.value = false
}

const handleBottomSectionResize = ({ width, height }) => {
  bottomSectionHeight.value = height
}

onMounted(() => {
  if (isDisplayModeSlideshow.value) {
    emit('load')
  }
})
</script>
<template>
  <div
    :class="[
      'w-full',
      pollCaption && isDisplayModeDefault && 'space-y-1.5',
      isDisplayModeSlideshow && 'rounded-2xl bg-light-ui-100',
      isDisplayModeExpanded && 'overflow-y-auto max-h-[calc(100vh-56px)]', // 56px is the height of the top bar on expanded view
      'font-sans',
    ]"
    @click.stop="
      (event) => isDisplayModeDefault && (didCurrentUserVoteForPoll || isPollClosed) && $emit('click', event)
    "
    @keydown.enter.stop="
      (event) => isDisplayModeDefault && (didCurrentUserVoteForPoll || isPollClosed) && $emit('enter', event)
    "
  >
    <OzBox
      class="w-full h-full relative overflow-hidden"
      :color="
        isDisplayModeSlideshow
          ? OzBoxColors.Primary
          : isDisplayModeExpanded
          ? OzBoxColors.None
          : toOzBoxColor(displayedBackgroundColor)
      "
      :dark-mode="darkMode"
      :radius="isDisplayModeSlideshow ? 16 : isStream ? 12 : 8"
    >
      <div
        ref="pollScrollableContainer"
        :class="[
          'w-full h-full box-border',
          isDisplayModeSlideshow && 'px-4 overflow-y-auto overflow-x-hidden', // on slideshow add extra padding so it looks like a post not just a poll
          {
            'scrollbar-thin': darkMode === false,
            'scrollbar-thin-dark': darkMode === true,
            'scrollbar-thin dark:scrollbar-thin-dark': darkMode === 'auto',
          },
        ]"
        :style="{
          paddingBottom: `${
            (isDisplayModeSlideshow || isDisplayModeDefault) &&
            interactionsDisabled !== SurfacePostPollChoiceInteractionsDisabled.All
              ? bottomSectionHeight
              : 0
          }px`,
        }"
      >
        <SurfacePostPollQuestion
          :dark-mode="darkMode"
          :question="poll.question"
          :display-mode="displayMode"
          :x-overlay="xOverlay"
          :is-stream="isStream"
          @dblclick="(event) => $emit('dblclick', event)"
        />
        <div
          ref="pollResultsContainer"
          :class="[
            'w-full',
            isDisplayModeExpandedOrSlideshow && 'pt-1',
            isDisplayModeExpandedOrSlideshow && {
              'rounded-2xl': true,
              'bg-light-ui-200': darkMode === false,
              'bg-dark-ui-200 ': darkMode === true,
              'bg-light-ui-200 dark:bg-dark-ui-200 ': darkMode === 'auto',
            },
          ]"
          @dblclick=";(isShowingPollResults || didCurrentUserVoteForPoll || isPollClosed) && $emit('dblclick', $event)"
        >
          <template v-if="isShowingPollResults || didCurrentUserVoteForPoll || isPollClosed">
            <SurfacePostPollResult
              v-for="(result, i) in pollResults"
              :key="`poll-${poll.poll_id}-result-${result.poll_choice_id}`"
              :index="i"
              :result="result"
              :poll-colors="pollColors"
              :is-stream="isStream"
              :display-mode="displayMode"
              :dark-mode="darkMode"
              :num-results="pollResults.length"
              :decimal-percentage="hasDecimalVoteRatio"
            />
          </template>
          <template v-else-if="isWhiteboard">
            <SurfacePostPollChoiceWhiteboard
              v-for="(choice, i) in poll.poll_choices"
              :key="`poll-${poll.poll_id}-choice-${choice.poll_choice_id}`"
              :index="i"
              :selected="choice.poll_choice_id === selectedPollChoiceId"
              :choice-text="choice.choice_text"
              :choice-id="choice.poll_choice_id"
              :poll-colors="pollColors"
              :is-stream="isStream"
              :display-mode="displayMode"
              :dark-mode="darkMode"
              :num-choices="poll.poll_choices.length"
              :interactions-disabled="interactionsDisabled"
              @select="(choiceId) => (selectedPollChoiceId = choiceId)"
            />
          </template>
          <template v-else>
            <SurfacePostPollChoice
              v-for="(choice, i) in poll.poll_choices"
              :key="`poll-${poll.poll_id}-choice-${choice.poll_choice_id}`"
              :index="i"
              :selected="choice.poll_choice_id === selectedPollChoiceId"
              :choice-text="choice.choice_text"
              :choice-id="choice.poll_choice_id"
              :poll-colors="pollColors"
              :is-stream="isStream"
              :display-mode="displayMode"
              :dark-mode="darkMode"
              :num-choices="poll.poll_choices.length"
              :interactions-disabled="interactionsDisabled"
              @select="(choiceId) => (selectedPollChoiceId = choiceId)"
            />
          </template>
        </div>
        <SurfacePostPollBottomSection
          v-if="
            (isDisplayModeDefault || isDisplayModeSlideshow) &&
            interactionsDisabled !== SurfacePostPollChoiceInteractionsDisabled.All
          "
          class="absolute bottom-0 start-0"
          :dark-mode="darkMode"
          :is-showing-poll-results="isShowingPollResults"
          :total-votes-count="totalVotesCount"
          :is-poll-closed="isPollClosed"
          :is-vote-button-disabled="selectedPollChoiceId === -1 || isVoting"
          :x-vote-button="!isPollClosed && !didCurrentUserVoteForPoll && !isShowingPollResults"
          :x-results-toggle="(canIModerate || isPostAuthor) && !isPollClosed && !didCurrentUserVoteForPoll"
          :is-slideshow="isDisplayModeSlideshow"
          :is-stream="isStream"
          :poll-scale-multiplier="pollScaleMultiplier"
          @toggle-poll-results="togglePollResults"
          @vote="vote"
          @resize="(resizedSectionDimensions) => handleBottomSectionResize(resizedSectionDimensions)"
          @dblclick="$emit('dblclick', $event)"
        />
      </div>
    </OzBox>
    <p
      v-if="pollCaption && isDisplayModeDefault"
      :class="[
        'text-body-extra-small no-drag ms-1',
        {
          'text-dark-text-300': darkMode === false,
          'text-light-text-300': darkMode === true,
          'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto',
        },
      ]"
      @dblclick="$emit('dblclick', $event)"
    >
      {{ pollCaption }}
    </p>
    <SurfacePostPollBottomSection
      v-if="isDisplayModeExpanded && interactionsDisabled !== SurfacePostPollChoiceInteractionsDisabled.All"
      :is-showing-poll-results="isShowingPollResults"
      :dark-mode="darkMode"
      :total-votes-count="totalVotesCount"
      :is-poll-closed="isPollClosed"
      :is-vote-button-disabled="selectedPollChoiceId === -1 || isVoting"
      :is-expanded="isDisplayModeExpanded"
      :x-vote-button="!isPollClosed && !didCurrentUserVoteForPoll && !isShowingPollResults"
      :x-results-toggle="(canIModerate || isPostAuthor) && !isPollClosed && !didCurrentUserVoteForPoll"
      @toggle-poll-results="togglePollResults"
      @vote="vote"
    />
  </div>
</template>
